import { debounce } from '@paintscout/ui'
import type { OptionsDocument } from 'paintscout'
import { useState, useCallback } from 'react'
import { useLocationSuggestions } from './useLocationSuggestions'

export interface UseAddressSearchOptions {
  options: OptionsDocument
  skip?: boolean
}

export interface UseAddressSearchResult {
  addressSearchResults: any[]
  fetching: boolean
  error: any
  locationQuery: string
  debouncedAddressSearch: (query: string) => void
}

/**
 * Hook for address autocomplete/search functionality
 * Handles debounced address search with location suggestions
 */
export function useAddressSearch({ options, skip = false }: UseAddressSearchOptions): UseAddressSearchResult {
  const [locationQuery, setLocationQuery] = useState('')
  const coordinates = options?.options?.companyCoordinates

  const handleAddressSearch = useCallback((query: string) => {
    setLocationQuery(query)
  }, [])

  const debouncedAddressSearch = useCallback(debounce(handleAddressSearch, 250), [handleAddressSearch])

  const cityValue = options?.options?.companyCity?.value
  const countryValue = options?.options?.companyCountry?.value

  const {
    suggestions,
    loading: fetching,
    error
  } = useLocationSuggestions({
    query: locationQuery,
    coordinates,
    city: typeof cityValue === 'string' ? cityValue : undefined,
    country: typeof countryValue === 'string' ? countryValue : undefined,
    skip: skip || locationQuery.length === 0
  })

  return {
    addressSearchResults: suggestions,
    fetching,
    error,
    locationQuery,
    debouncedAddressSearch
  }
}
